export interface WeatherAPIEndpoint {
  name: string;
  baseUrl: string;
  apiKey: string;
  priority: number;
  rateLimit: {
    requestsPerMinute: number;
    requestsPerDay: number;
  };
  timeout: number;
  isActive: boolean;
}

export interface LocationAPIEndpoint {
  name: string;
  baseUrl: string;
  apiKey?: string;
  priority: number;
  timeout: number;
  isActive: boolean;
}

export interface WeatherConfig {
  environment: 'development' | 'production' | 'test';
  weatherAPIs: WeatherAPIEndpoint[];
  locationAPIs: LocationAPIEndpoint[];
  weatherCacheDuration: number;
  locationCacheDuration: number;
  enableRetry: boolean;
  maxRetryAttempts: number;
  requestTimeout: number;
  enableAPIRotation: boolean;
  enableFallbackServices: boolean;
  debugMode: boolean;
}

export class WeatherConfigManager {
  private static config: WeatherConfig | null = null;
  private static apiUsageTracker = new Map<string, { count: number; lastReset: number }>();

  static getConfig(): WeatherConfig {
    if (!this.config) {
      this.config = this.loadConfig();
    }
    return this.config;
  }

  private static loadConfig(): WeatherConfig {
    const environment = this.getEnvironment();
    
    return {
      environment,
      weatherAPIs: this.loadWeatherAPIs(),
      locationAPIs: this.loadLocationAPIs(),
      weatherCacheDuration: parseInt(import.meta.env.VITE_WEATHER_CACHE_DURATION) || 30,
      locationCacheDuration: parseInt(import.meta.env.VITE_LOCATION_CACHE_DURATION) || 60,
      enableRetry: import.meta.env.VITE_WEATHER_ENABLE_RETRY !== 'false',
      maxRetryAttempts: parseInt(import.meta.env.VITE_WEATHER_MAX_RETRY_ATTEMPTS) || 3,
      requestTimeout: parseInt(import.meta.env.VITE_WEATHER_REQUEST_TIMEOUT) || 8000,
      enableAPIRotation: import.meta.env.VITE_WEATHER_ENABLE_API_ROTATION !== 'false',
      enableFallbackServices: import.meta.env.VITE_WEATHER_ENABLE_FALLBACK_SERVICES !== 'false',
      debugMode: environment === 'development' || import.meta.env.VITE_WEATHER_DEBUG_MODE === 'true',
    };
  }

  private static getEnvironment(): 'development' | 'production' | 'test' {
    if (import.meta.env.MODE === 'test') return 'test';
    if (import.meta.env.DEV) return 'development';
    return 'production';
  }

  private static loadWeatherAPIs(): WeatherAPIEndpoint[] {
    const apis: WeatherAPIEndpoint[] = [];

    // Primary OpenWeatherMap API
    const openWeatherKey = import.meta.env.VITE_OPENWEATHER_API_KEY;
    const openWeatherEnabled = import.meta.env.VITE_OPENWEATHER_ENABLED !== 'false';
    if (openWeatherKey && openWeatherKey !== 'your_openweather_api_key_here' && openWeatherEnabled) {
      apis.push({
        name: 'openweathermap',
        baseUrl: 'https://api.openweathermap.org/data/2.5/weather',
        apiKey: openWeatherKey,
        priority: 1,
        rateLimit: {
          requestsPerMinute: 60,
          requestsPerDay: 1000,
        },
        timeout: parseInt(import.meta.env.VITE_OPENWEATHER_TIMEOUT) || 8000,
        isActive: true,
      });
    }

    // Secondary OpenWeatherMap API (if different key provided)
    const openWeatherKey2 = import.meta.env.VITE_OPENWEATHER_API_KEY_2;
    const openWeather2Enabled = import.meta.env.VITE_OPENWEATHER_2_ENABLED !== 'false';
    if (openWeatherKey2 && openWeatherKey2 !== 'your_secondary_openweather_api_key_here' && 
        openWeatherKey2 !== openWeatherKey && openWeather2Enabled) {
      apis.push({
        name: 'openweathermap-2',
        baseUrl: 'https://api.openweathermap.org/data/2.5/weather',
        apiKey: openWeatherKey2,
        priority: 2,
        rateLimit: {
          requestsPerMinute: 60,
          requestsPerDay: 1000,
        },
        timeout: parseInt(import.meta.env.VITE_OPENWEATHER_TIMEOUT) || 8000,
        isActive: true,
      });
    }

    // WeatherAPI.com (alternative service)
    const weatherApiKey = import.meta.env.VITE_WEATHERAPI_COM_KEY;
    const weatherApiEnabled = import.meta.env.VITE_WEATHERAPI_ENABLED !== 'false';
    if (weatherApiKey && weatherApiKey !== 'your_weatherapi_com_key_here' && weatherApiEnabled) {
      apis.push({
        name: 'weatherapi-com',
        baseUrl: 'https://api.weatherapi.com/v1/current.json',
        apiKey: weatherApiKey,
        priority: 3,
        rateLimit: {
          requestsPerMinute: 100,
          requestsPerDay: 1000000,
        },
        timeout: parseInt(import.meta.env.VITE_WEATHERAPI_TIMEOUT) || 8000,
        isActive: true,
      });
    }

    // Sort by priority
    return apis.sort((a, b) => a.priority - b.priority);
  }

  private static loadLocationAPIs(): LocationAPIEndpoint[] {
    const apis: LocationAPIEndpoint[] = [];

    // Primary: ipapi.co (free tier)
    const ipapiCoEnabled = import.meta.env.VITE_IPAPI_CO_ENABLED !== 'false';
    if (ipapiCoEnabled) {
      apis.push({
        name: 'ipapi-co',
        baseUrl: 'https://ipapi.co/json',
        priority: 1,
        timeout: parseInt(import.meta.env.VITE_LOCATION_TIMEOUT) || 5000,
        isActive: true,
      });
    }

    // Secondary: ip-api.com (free tier)
    const ipApiComEnabled = import.meta.env.VITE_IP_API_COM_ENABLED !== 'false';
    if (ipApiComEnabled) {
      apis.push({
        name: 'ip-api-com',
        baseUrl: 'http://ip-api.com/json',
        priority: 2,
        timeout: parseInt(import.meta.env.VITE_LOCATION_TIMEOUT) || 5000,
        isActive: true,
      });
    }

    // Tertiary: ipgeolocation.io (requires API key)
    const ipGeoKey = import.meta.env.VITE_IPGEOLOCATION_API_KEY;
    const ipGeoEnabled = import.meta.env.VITE_IPGEOLOCATION_ENABLED !== 'false';
    if (ipGeoKey && ipGeoKey !== 'your_ipgeolocation_api_key_here' && ipGeoEnabled) {
      apis.push({
        name: 'ipgeolocation-io',
        baseUrl: 'https://api.ipgeolocation.io/ipgeo',
        apiKey: ipGeoKey,
        priority: 3,
        timeout: parseInt(import.meta.env.VITE_IPGEOLOCATION_TIMEOUT) || 5000,
        isActive: true,
      });
    }

    return apis.sort((a, b) => a.priority - b.priority);
  }

  static getActiveWeatherAPIs(): WeatherAPIEndpoint[] {
    const config = this.getConfig();
    return config.weatherAPIs.filter(api => api.isActive);
  }

  static getActiveLocationAPIs(): LocationAPIEndpoint[] {
    const config = this.getConfig();
    return config.locationAPIs.filter(api => api.isActive);
  }

  static getNextAvailableWeatherAPI(): WeatherAPIEndpoint | null {
    const activeAPIs = this.getActiveWeatherAPIs();
    
    if (!activeAPIs.length) {
      return null;
    }

    // If rotation is disabled, always use the first (highest priority) API
    if (!this.getConfig().enableAPIRotation) {
      return activeAPIs[0];
    }

    // Find the first API that hasn't exceeded its rate limits
    for (const api of activeAPIs) {
      if (this.isAPIAvailable(api)) {
        return api;
      }
    }

    // If all APIs are rate limited, return the highest priority one
    // (it might have reset by now)
    return activeAPIs[0];
  }

  static getNextAvailableLocationAPI(): LocationAPIEndpoint | null {
    const activeAPIs = this.getActiveLocationAPIs();
    
    if (!activeAPIs.length) {
      return null;
    }

    // If rotation is disabled, always use the first (highest priority) API
    if (!this.getConfig().enableAPIRotation) {
      return activeAPIs[0];
    }

    // For location APIs, just return the first active one
    // (they typically have generous rate limits)
    return activeAPIs[0];
  }

  private static isAPIAvailable(api: WeatherAPIEndpoint): boolean {
    const usage = this.apiUsageTracker.get(api.name);
    if (!usage) {
      return true;
    }

    const now = Date.now();
    const minutesSinceReset = (now - usage.lastReset) / (1000 * 60);

    // Reset counter if more than a minute has passed
    if (minutesSinceReset >= 1) {
      this.apiUsageTracker.set(api.name, { count: 0, lastReset: now });
      return true;
    }

    // Check if we're under the rate limit
    return usage.count < api.rateLimit.requestsPerMinute;
  }

  static trackAPIUsage(apiName: string): void {
    const usage = this.apiUsageTracker.get(apiName) || { count: 0, lastReset: Date.now() };
    usage.count++;
    this.apiUsageTracker.set(apiName, usage);
  }

  static isConfigured(): boolean {
    const activeWeatherAPIs = this.getActiveWeatherAPIs();
    return activeWeatherAPIs.length > 0;
  }

  static validateConfig(): { isValid: boolean; errors: string[]; warnings: string[] } {
    const config = this.getConfig();
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if we have at least one weather API configured
    const activeWeatherAPIs = this.getActiveWeatherAPIs();
    if (activeWeatherAPIs.length === 0) {
      errors.push('No active weather APIs configured');
    }

    // Check if we have at least one location API configured
    const activeLocationAPIs = this.getActiveLocationAPIs();
    if (activeLocationAPIs.length === 0) {
      errors.push('No active location APIs configured');
    }

    // Validate cache durations
    if (config.weatherCacheDuration < 1) {
      errors.push('Weather cache duration must be at least 1 minute');
    }

    if (config.locationCacheDuration < 1) {
      errors.push('Location cache duration must be at least 1 minute');
    }

    // Validate retry settings
    if (config.maxRetryAttempts < 1) {
      errors.push('Max retry attempts must be at least 1');
    }

    if (config.requestTimeout < 1000) {
      errors.push('Request timeout must be at least 1000ms');
    }

    // Warnings for production environment
    if (config.environment === 'production') {
      if (activeWeatherAPIs.length === 1) {
        warnings.push('Only one weather API configured for production. Consider adding backup APIs.');
      }

      if (config.debugMode) {
        warnings.push('Debug mode is enabled in production environment');
      }

      if (!config.enableAPIRotation) {
        warnings.push('API rotation is disabled in production environment');
      }
    }

    // Warnings for development environment
    if (config.environment === 'development') {
      if (activeWeatherAPIs.length === 0) {
        warnings.push('No weather APIs configured for development. Weather features will use fallback data.');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  static getConfigSummary(): {
    environment: string;
    weatherAPIs: { name: string; isActive: boolean; priority: number }[];
    locationAPIs: { name: string; isActive: boolean; priority: number }[];
    features: {
      apiRotation: boolean;
      fallbackServices: boolean;
      debugMode: boolean;
      retryEnabled: boolean;
    };
  } {
    const config = this.getConfig();
    
    return {
      environment: config.environment,
      weatherAPIs: config.weatherAPIs.map(api => ({
        name: api.name,
        isActive: api.isActive,
        priority: api.priority,
      })),
      locationAPIs: config.locationAPIs.map(api => ({
        name: api.name,
        isActive: api.isActive,
        priority: api.priority,
      })),
      features: {
        apiRotation: config.enableAPIRotation,
        fallbackServices: config.enableFallbackServices,
        debugMode: config.debugMode,
        retryEnabled: config.enableRetry,
      },
    };
  }

  static resetAPIUsageTracking(): void {
    this.apiUsageTracker.clear();
  }

  // Development/testing utilities
  static forceReloadConfig(): void {
    this.config = null;
    this.resetAPIUsageTracking();
  }

  static setTestConfig(testConfig: Partial<WeatherConfig>): void {
    if (this.getEnvironment() !== 'test') {
      console.warn('setTestConfig should only be used in test environment');
      return;
    }
    
    this.config = {
      ...this.loadConfig(),
      ...testConfig,
    };
  }
}